/**
 * Payout analysis API — query params and response normalization.
 * @see docs/payout-analysis-frontend.md
 */

import { fetchPayoutAnalysis } from "api/pricerApi";

/** Default metric when `metric` is omitted (backend returns expiry + snapshot lines). */
export const PAYOUT_ANALYSIS_DEFAULT_METRIC = "P&L";

/** Allowed `metric` query values (must match backend). */
export const PAYOUT_ANALYSIS_METRICS = {
  PNL: "P&L",
  DELTA: "delta",
  GAMMA: "gamma",
  VEGA: "vega",
  THETA: "theta",
  RHO: "Rho",
  SKEW_DELTA: "Skew Delta",
  BREAKEVEN: "Breakeven",
  TOTAL_PREMIUM: "Total Premium",
  TOTAL_QUANTITY: "Total Quantity",
};

/** Chart tab keys → API `metric` query values. */
export const PAYOUT_CHART_TAB_TO_METRIC = {
  pnl: PAYOUT_ANALYSIS_METRICS.PNL,
  delta: PAYOUT_ANALYSIS_METRICS.DELTA,
  gamma: PAYOUT_ANALYSIS_METRICS.GAMMA,
  vega: PAYOUT_ANALYSIS_METRICS.VEGA,
  theta: PAYOUT_ANALYSIS_METRICS.THETA,
  rho: PAYOUT_ANALYSIS_METRICS.RHO,
  skew_delta: PAYOUT_ANALYSIS_METRICS.SKEW_DELTA,
  breakeven: PAYOUT_ANALYSIS_METRICS.BREAKEVEN,
  total_premium: PAYOUT_ANALYSIS_METRICS.TOTAL_PREMIUM,
  total_quantity: PAYOUT_ANALYSIS_METRICS.TOTAL_QUANTITY,
};

export function getApiMetricForChartTab(tabKey) {
  return (
    PAYOUT_CHART_TAB_TO_METRIC[tabKey] ?? PAYOUT_ANALYSIS_DEFAULT_METRIC
  );
}

export function getChartTabKeyForApiMetric(metric) {
  if (!metric) return "pnl";
  const normalized = String(metric).toLowerCase();
  const entry = Object.entries(PAYOUT_CHART_TAB_TO_METRIC).find(
    ([, value]) => String(value).toLowerCase() === normalized
  );
  return entry?.[0] ?? "pnl";
}

/** Whether a raw analysis payload belongs to the requested API metric. */
export function payoutAnalysisMatchesMetric(rawData, metric) {
  if (!metric) return false;
  const raw = unwrapPayoutAnalysisRaw(rawData);
  if (!raw) return false;
  const selected = raw.selected_metric;
  if (!selected) return false;
  return normalizeApiMetricKey(selected) === normalizeApiMetricKey(metric);
}

export function getPayoutAnalysisDaysElapsed(rawData) {
  const raw = unwrapPayoutAnalysisRaw(rawData);
  if (!raw) return null;
  const n = raw?.left_side?.days_elapsed ?? raw?.grid?.days_elapsed;
  return typeof n === "number" && !Number.isNaN(n) ? n : null;
}

/**
 * Pick the analysis payload to render for the active metric tab.
 * Uses fetched/cache/previous same-metric data; bootstrap only as a last-resort
 * first-load placeholder so a days-elapsed or Bid/Ask refetch does not flash day-0.
 */
export function resolvePayoutAnalysisMetricData({
  fetchedData,
  cachedData,
  previousData,
  payoutBootstrap,
  apiMetric,
  useBootstrapPlaceholder = false,
}) {
  for (const candidate of [fetchedData, cachedData, previousData]) {
    if (payoutAnalysisMatchesMetric(candidate, apiMetric)) {
      return unwrapPayoutAnalysisRaw(candidate);
    }
  }
  if (
    useBootstrapPlaceholder &&
    payoutAnalysisMatchesMetric(payoutBootstrap, apiMetric)
  ) {
    return unwrapPayoutAnalysisRaw(payoutBootstrap) ?? payoutBootstrap;
  }
  return null;
}

/** @deprecated Use resolvePayoutAnalysisMetricData */
export const resolveSharePanelAnalysisData = resolvePayoutAnalysisMetricData;

/** Chart tab metadata used by `PayoutAnalysisChartTabs`. */
export const PAYOUT_CHART_TAB_CONFIGS = [
  { key: "pnl", label: "P&L", kind: "pnl" },
  { key: "delta", label: "Delta", kind: "greek", seriesKey: "delta", unitKey: "delta" },
  { key: "gamma", label: "Gamma", kind: "greek", seriesKey: "gamma", unitKey: "gamma" },
  { key: "vega", label: "Vega", kind: "greek", seriesKey: "vega", unitKey: "vega" },
  { key: "theta", label: "Theta", kind: "greek", seriesKey: "theta", unitKey: "theta" },
  { key: "rho", label: "Rho", kind: "greek", seriesKey: "rho", unitKey: "rho" },
  {
    key: "skew_delta",
    label: "Skew Delta",
    kind: "greek",
    seriesKey: "skew_delta",
    unitKey: "skew_delta",
  },
  {
    key: "breakeven",
    label: "Breakeven",
    kind: "breakeven",
    seriesKey: "breakeven",
    unitKey: "breakeven",
  },
  {
    key: "total_premium",
    label: "Total Premium",
    kind: "summary",
    summaryTitle: "Premium",
  },
  {
    key: "total_quantity",
    label: "Total Quantity",
    kind: "summary",
    summaryTitle: "Quantity",
  },
];

function normalizeApiMetricKey(metricKey) {
  return String(metricKey ?? "").trim().toLowerCase();
}

/** Unwrap axios `{ data: { data } }` or `{ message, data }` to the analysis payload. */
export function unwrapPayoutAnalysisRaw(queryResult) {
  if (!queryResult || typeof queryResult !== "object") return null;
  if ("selected_metric" in queryResult || Array.isArray(queryResult.lines)) {
    return queryResult;
  }
  const body = queryResult.data ?? queryResult;
  const raw = body?.data ?? body;
  return raw && typeof raw === "object" ? raw : null;
}

/** Whether the API `selected_metric` matches a chart tab key. */
export function chartTabMatchesSelectedMetric(chartTabKey, selectedMetric) {
  if (!selectedMetric) return false;
  return (
    normalizeApiMetricKey(selectedMetric) ===
    normalizeApiMetricKey(getApiMetricForChartTab(chartTabKey))
  );
}

/**
 * Build chart tabs from API `available_metrics` (preserves API order and labels).
 * Falls back to all known tabs when the list is empty.
 */
export function getAvailableChartTabs(availableMetrics) {
  if (!Array.isArray(availableMetrics) || availableMetrics.length === 0) {
    return PAYOUT_CHART_TAB_CONFIGS;
  }

  const tabByApiMetric = new Map(
    PAYOUT_CHART_TAB_CONFIGS.map((tab) => [
      normalizeApiMetricKey(getApiMetricForChartTab(tab.key)),
      tab,
    ])
  );

  const seen = new Set();
  const tabs = [];

  for (const metric of availableMetrics) {
    const apiKey = normalizeApiMetricKey(metric?.key ?? metric);
    if (!apiKey || seen.has(apiKey)) continue;

    const config = tabByApiMetric.get(apiKey);
    if (!config) continue;

    seen.add(apiKey);
    tabs.push({
      ...config,
      label: metric?.label ?? config.label,
    });
  }

  return tabs.length ? tabs : [PAYOUT_CHART_TAB_CONFIGS[0]];
}

export function getDefaultChartTabKey(availableMetrics) {
  if (!Array.isArray(availableMetrics) || availableMetrics.length === 0) {
    return "pnl";
  }

  const defaultMetric = availableMetrics.find((metric) => metric?.is_default);
  const metricKey = defaultMetric?.key ?? availableMetrics[0]?.key;
  return getChartTabKeyForApiMetric(metricKey);
}

export function isChartTabAvailable(chartTabKey, availableMetrics) {
  return getAvailableChartTabs(availableMetrics).some((tab) => tab.key === chartTabKey);
}

const EXPIRY_LINE_LABEL = "Payoff at expiry";

const API_METRIC_TO_SERIES_KEY = {
  [PAYOUT_ANALYSIS_METRICS.PNL]: "pnl",
  [PAYOUT_ANALYSIS_METRICS.DELTA]: "delta",
  [PAYOUT_ANALYSIS_METRICS.GAMMA]: "gamma",
  [PAYOUT_ANALYSIS_METRICS.VEGA]: "vega",
  [PAYOUT_ANALYSIS_METRICS.THETA]: "theta",
  [PAYOUT_ANALYSIS_METRICS.RHO]: "rho",
  [PAYOUT_ANALYSIS_METRICS.SKEW_DELTA]: "skew_delta",
  [PAYOUT_ANALYSIS_METRICS.BREAKEVEN]: "breakeven",
  [PAYOUT_ANALYSIS_METRICS.TOTAL_PREMIUM]: "total_premium",
  [PAYOUT_ANALYSIS_METRICS.TOTAL_QUANTITY]: "total_quantity",
};

/**
 * Build GET query params; omits null/undefined/empty overrides.
 * @param {object} [options]
 * @param {string} [options.metric]
 * @param {number} [options.chart_increment]
 * @param {number} [options.starting_ul_settlement_price]
 * @param {number} [options.points_count]
 * @param {number} [options.days_elapsed]
 * @param {number} [options.volatility_override] decimal, e.g. 0.25
 * @param {number} [options.risk_free_override]
 * @param {string} [options.side] — `bid` | `ask`
 * @param {string} [options.popup_session] — freeze token from the open popup
 */
export function buildPayoutAnalysisQueryParams(options = {}) {
  const {
    metric = PAYOUT_ANALYSIS_DEFAULT_METRIC,
    chart_increment,
    starting_ul_settlement_price,
    points_count,
    days_elapsed,
    volatility_override,
    risk_free_override,
    side,
    popup_session,
  } = options;

  const params = {};

  if (metric != null && String(metric).trim() !== "") {
    params.metric = metric;
  }

  if (
    chart_increment != null &&
    chart_increment !== "" &&
    Number(chart_increment) > 0
  ) {
    params.chart_increment = chart_increment;
  }

  if (
    starting_ul_settlement_price != null &&
    starting_ul_settlement_price !== "" &&
    !Number.isNaN(Number(starting_ul_settlement_price))
  ) {
    params.starting_ul_settlement_price = starting_ul_settlement_price;
  }

  if (
    points_count != null &&
    points_count !== "" &&
    !Number.isNaN(Number(points_count))
  ) {
    params.points_count = points_count;
  }

  if (days_elapsed != null && days_elapsed !== "" && !Number.isNaN(Number(days_elapsed))) {
    params.days_elapsed = days_elapsed;
  }

  if (
    volatility_override != null &&
    volatility_override !== "" &&
    !Number.isNaN(Number(volatility_override))
  ) {
    params.volatility_override = volatility_override;
  }

  if (
    risk_free_override != null &&
    risk_free_override !== "" &&
    !Number.isNaN(Number(risk_free_override))
  ) {
    params.risk_free_override = risk_free_override;
  }

  if (side === "bid" || side === "ask") {
    params.side = side;
  }

  if (typeof popup_session === "string" && popup_session.trim() !== "") {
    params.popup_session = popup_session.trim();
  }

  return params;
}

/** Leg summary rows from analysis or payout bootstrap payloads. */
export function getAnalysisQuantitySummary(raw) {
  if (!raw || typeof raw !== "object") return [];
  return (
    raw?.left_side?.quantity ??
    raw?.right_side?.quantity ??
    raw?.summary ??
    []
  );
}

/** Unique forward (x) values from all `lines[].data` points, sorted ascending. */
export function extractForwardPricesFromLines(lines) {
  const xs = new Set();
  (lines ?? []).forEach((line) => {
    (line.data ?? []).forEach(([x]) => {
      const n = Number(x);
      if (!Number.isNaN(n)) xs.add(n);
    });
  });
  return [...xs].sort((a, b) => a - b);
}

/**
 * Unified `grid` for charts/controls — supports legacy `grid` or new `left_side` + `lines`.
 */
export function buildAnalysisGrid(raw) {
  const existing = raw?.grid;
  const left = raw?.left_side;
  const fromLines = extractForwardPricesFromLines(raw?.lines);
  const forward_prices =
    existing?.forward_prices?.length > 0 ? existing.forward_prices : fromLines;

  return {
    ...(existing ?? {}),
    forward_prices,
    days_elapsed: left?.days_elapsed ?? existing?.days_elapsed,
    days_to_expiry: left?.days_to_expiry ?? existing?.days_to_expiry,
    snapshot_calculation_date: existing?.snapshot_calculation_date,
    future_price: left?.future_price ?? existing?.future_price,
  };
}

/** Parse numeric UL from leg summary row `Underlying` (e.g. `"1 EUR/t"`). */
export function parseUnderlyingFromSummary(summary) {
  const row = summary?.find((r) => r.title === "Underlying");
  const raw = row?.legs?.[0]?.value;
  if (raw == null || raw === "") return null;
  const match = String(raw).match(/-?[\d,.]+/);
  if (!match) return null;
  return parseFloat(match[0].replace(/,/g, ""));
}

export function isDegenerateForwardGrid(analysisData) {
  const prices =
    analysisData?.grid?.forward_prices?.length > 0
      ? analysisData.grid.forward_prices
      : extractForwardPricesFromLines(analysisData?.lines);
  if (prices.length <= 1) return true;
  const first = Number(prices[0]);
  return prices.every((p) => Number(p) === first);
}

function linesHaveSingleForwardPrice(analysisData) {
  return (analysisData?.lines ?? []).some((line) => {
    const xs = new Set(
      (line.data ?? [])
        .map(([x]) => Number(x))
        .filter((x) => !Number.isNaN(x))
    );
    return (line.data?.length ?? 0) > 1 && xs.size <= 1;
  });
}

export function hasPayoutAnalysisChartLines(analysisData) {
  if (!analysisData || typeof analysisData !== "object") return false;

  const lines = analysisData.lines ?? [];
  if (
    lines.some((line) =>
      (line?.data ?? []).some(([x, y]) => {
        const xn = Number(x);
        const yn = Number(y);
        return !Number.isNaN(xn) && !Number.isNaN(yn);
      })
    )
  ) {
    return true;
  }

  const series = analysisData.series ?? {};
  return Object.values(series).some((entry) => (entry?.total?.length ?? 0) > 0);
}

/** True when the forward grid cannot draw a meaningful x-axis spread. */
export function isDegeneratePayoutAnalysisData(analysisData) {
  if (!analysisData) return false;
  if (isDegenerateForwardGrid(analysisData)) return true;
  if (linesHaveSingleForwardPrice(analysisData)) return true;
  const inc = Number(analysisData?.chart_increment?.value ?? 0);
  return inc === 0 && analysisData?.chart_increment?.is_default === true;
}

export function hasMeaningfulGridParams(gridParams = {}) {
  return (
    (gridParams.chart_increment != null && gridParams.chart_increment > 0) ||
    (gridParams.starting_ul_settlement_price != null &&
      !Number.isNaN(Number(gridParams.starting_ul_settlement_price)))
  );
}

/**
 * Grid query params for small/default UL when backend `chart_increment` is 0.
 */
export function buildProactiveGridParams({ underlying, startingSettlementPrice } = {}) {
  const ul = underlying ?? startingSettlementPrice;
  const parsed = Number(ul);
  if (ul == null || ul === "" || Number.isNaN(parsed) || parsed <= 0) {
    return {};
  }

  const chart_increment = Math.max(Number((parsed / 30).toFixed(4)), 0.01);
  const starting_ul_settlement_price = Math.max(
    0,
    Number((parsed - 20 * chart_increment).toFixed(4))
  );

  return { chart_increment, starting_ul_settlement_price };
}

/** Apply shared chart-control values; user overrides always win. */
export function mergeChartControlOverrides(params = {}, overrides = {}) {
  const merged = { ...params };
  const { chart_increment, starting_ul_settlement_price } = overrides;

  if (
    chart_increment != null &&
    chart_increment !== "" &&
    Number(chart_increment) > 0
  ) {
    merged.chart_increment = Number(chart_increment);
  }

  if (
    starting_ul_settlement_price != null &&
    starting_ul_settlement_price !== "" &&
    !Number.isNaN(Number(starting_ul_settlement_price))
  ) {
    merged.starting_ul_settlement_price = Number(starting_ul_settlement_price);
  }

  return merged;
}

/**
 * Prefer payout-tab grid values; when increment is default/0, derive spread params up front.
 * `overrides` are the debounced shared chart controls (Bid Payout + Analysis).
 */
export function buildAnalysisGridParamsFromPayoutBootstrap(
  payoutBootstrap,
  overrides = {}
) {
  const { chart_increment: chartIncrementOverride, starting_ul_settlement_price: startingUlOverride } =
    overrides;

  if (!payoutBootstrap) {
    return mergeChartControlOverrides({}, overrides);
  }

  const underlying = parseUnderlyingFromSummary(
    getAnalysisQuantitySummary(payoutBootstrap)
  );
  const startingSettlementPrice =
    startingUlOverride ?? payoutBootstrap.starting_settlement_price;
  const incrementMeta = payoutBootstrap.chart_increment;

  let params = {};

  if (
    chartIncrementOverride != null &&
    chartIncrementOverride !== "" &&
    Number(chartIncrementOverride) > 0
  ) {
    params = { chart_increment: Number(chartIncrementOverride) };
    if (
      startingUlOverride != null &&
      startingUlOverride !== "" &&
      !Number.isNaN(Number(startingUlOverride))
    ) {
      params.starting_ul_settlement_price = Number(startingUlOverride);
    } else if (
      startingSettlementPrice != null &&
      !Number.isNaN(Number(startingSettlementPrice))
    ) {
      params.starting_ul_settlement_price = Number(startingSettlementPrice);
    }
    return mergeChartControlOverrides(params, overrides);
  }

  if (
    incrementMeta?.value != null &&
    Number(incrementMeta.value) > 0 &&
    incrementMeta.is_default !== true
  ) {
    params = { chart_increment: incrementMeta.value };
    if (
      startingSettlementPrice != null &&
      !Number.isNaN(Number(startingSettlementPrice))
    ) {
      params.starting_ul_settlement_price = Number(startingSettlementPrice);
    }
    return mergeChartControlOverrides(params, overrides);
  }

  params = buildProactiveGridParams({ underlying, startingSettlementPrice });
  return mergeChartControlOverrides(params, overrides);
}

/**
 * Single analysis request with grid params applied up front (no probe + retry).
 */
export async function fetchPayoutAnalysisResolved(summaryId, options = {}) {
  const params = buildPayoutAnalysisQueryParams(options);
  const response = await fetchPayoutAnalysis(summaryId, params);
  return unwrapPayoutAnalysisRaw(response);
}

function isLegacySeriesShape(raw) {
  return raw?.series != null && raw?.lines == null;
}

function findLine(lines, label) {
  return (lines ?? []).find((line) => line?.label === label);
}

function findSnapshotLine(lines) {
  return (lines ?? []).find(
    (line) =>
      line?.label &&
      line.label !== EXPIRY_LINE_LABEL &&
      (line.label === "Snapshot" ||
        line.label.startsWith("Total") ||
        line.label.startsWith("Day "))
  );
}

/** Unescape API tooltip text (`+\/-` → `+/-`). */
export function formatPayoutLineTooltip(text) {
  if (text == null) return null;
  const formatted = String(text).replace(/\\\//g, "/").trim();
  return formatted || null;
}

function lineToSeriesEntry(line) {
  if (!line?.data?.length) return null;
  const tooltip = formatPayoutLineTooltip(line.tooltip);
  return tooltip ? { total: line.data, tooltip } : { total: line.data };
}

/**
 * Map API `lines` + `selected_metric` into legacy `series` shape used by chart helpers.
 */
export function buildSeriesFromLines(raw) {
  const lines = raw?.lines ?? [];
  const selectedMetric = raw?.selected_metric ?? PAYOUT_ANALYSIS_DEFAULT_METRIC;
  const series = {};
  let pnlSnapshotLabel = null;

  if (selectedMetric === PAYOUT_ANALYSIS_METRICS.PNL) {
    const expiryLine = findLine(lines, EXPIRY_LINE_LABEL);
    const snapshotLine = findSnapshotLine(lines);
    const expiryEntry = lineToSeriesEntry(expiryLine);
    const snapshotEntry = lineToSeriesEntry(snapshotLine);
    if (expiryEntry) series.pnl_expiry = expiryEntry;
    if (snapshotEntry) {
      series.pnl_snapshot = snapshotEntry;
      pnlSnapshotLabel = snapshotLine?.label ?? null;
    }
    return { series, pnlSnapshotLabel };
  }

  if (selectedMetric === PAYOUT_ANALYSIS_METRICS.BREAKEVEN) {
    const legLines = lines.filter((line) => line?.data?.length);
    if (legLines.length) {
      series.breakeven_legs = legLines.map((line) => ({
        label: line.label,
        total: line.data,
      }));
      const entry = lineToSeriesEntry(legLines[0]);
      if (entry) series.breakeven = entry;
    }
    return { series, pnlSnapshotLabel: null };
  }

  const seriesKey = API_METRIC_TO_SERIES_KEY[selectedMetric] ?? selectedMetric;
  const entry = lineToSeriesEntry(lines[0]);
  if (entry) {
    series[seriesKey] = entry;
  }

  return { series, pnlSnapshotLabel: null };
}

/**
 * Normalize `{ message, data }` payload or raw `data` object for existing UI consumers.
 * Preserves raw API fields (`lines`, `available_metrics`, etc.) and adds legacy aliases.
 */
export function normalizePayoutAnalysisResponse(raw) {
  if (!raw || typeof raw !== "object") return null;

  if (isLegacySeriesShape(raw)) {
    return {
      ...raw,
      summary: getAnalysisQuantitySummary(raw),
      grid: buildAnalysisGrid(raw),
    };
  }

  const summary = getAnalysisQuantitySummary(raw);
  const grid = buildAnalysisGrid(raw);
  const { series, pnlSnapshotLabel } = buildSeriesFromLines(raw);
  const selectedMetric = raw.selected_metric ?? PAYOUT_ANALYSIS_DEFAULT_METRIC;
  const metric_line_label =
    selectedMetric !== PAYOUT_ANALYSIS_METRICS.PNL
      ? raw?.lines?.[0]?.label ?? null
      : null;

  return {
    ...raw,
    summary,
    grid,
    series,
    pnl_snapshot_label: pnlSnapshotLabel,
    metric_line_label,
    kpis: raw.kpis ?? { breakevens: raw.breakevens ?? [] },
    available_metrics: raw.available_metrics ?? [],
    selected_metric: raw.selected_metric ?? PAYOUT_ANALYSIS_DEFAULT_METRIC,
  };
}

/**
 * Unwrap axios response: `{ message, data: { ... } }`.
 * @param {object} queryResult — react-query data or axios response
 */
export function getPayoutAnalysisData(queryResult) {
  return normalizePayoutAnalysisResponse(unwrapPayoutAnalysisRaw(queryResult));
}

/**
 * Y-axis unit for a metric key from `units` map.
 * @param {object} units
 * @param {string} metricKey — API metric key e.g. `P&L`, `delta`, `Rho`
 */
export function getMetricUnit(units, metricKey) {
  if (!units || !metricKey) return null;

  const unitKeyMap = {
    [PAYOUT_ANALYSIS_METRICS.PNL]: "payout",
    [PAYOUT_ANALYSIS_METRICS.DELTA]: "delta",
    [PAYOUT_ANALYSIS_METRICS.GAMMA]: "gamma",
    [PAYOUT_ANALYSIS_METRICS.VEGA]: "vega",
    [PAYOUT_ANALYSIS_METRICS.THETA]: "theta",
    [PAYOUT_ANALYSIS_METRICS.RHO]: "rho",
    [PAYOUT_ANALYSIS_METRICS.SKEW_DELTA]: "skew_delta",
    [PAYOUT_ANALYSIS_METRICS.BREAKEVEN]: "breakeven",
    [PAYOUT_ANALYSIS_METRICS.TOTAL_PREMIUM]: "total_premium",
    [PAYOUT_ANALYSIS_METRICS.TOTAL_QUANTITY]: "total_quantity",
  };

  const unitsKey = unitKeyMap[metricKey] ?? metricKey;
  return units[unitsKey] ?? units[metricKey] ?? null;
}

/**
 * Build Highcharts-ready series list from API `lines` (no legacy `series` required).
 */
export function buildChartSeriesListFromLines(analysisData, { pnlSnapshotLabel } = {}) {
  const lines = analysisData?.lines ?? [];
  const selectedMetric =
    analysisData?.selected_metric ?? PAYOUT_ANALYSIS_DEFAULT_METRIC;

  if (selectedMetric === PAYOUT_ANALYSIS_METRICS.PNL) {
    const expiryLine = findLine(lines, EXPIRY_LINE_LABEL);
    const snapshotLine = findSnapshotLine(lines);
    const snapshotName =
      pnlSnapshotLabel ??
      analysisData?.pnl_snapshot_label ??
      snapshotLine?.label ??
      "Snapshot";

    const result = [];
    if (expiryLine?.data?.length) {
      result.push({
        name: EXPIRY_LINE_LABEL,
        data: expiryLine.data,
        dashStyle: "Dash",
        lineWidth: 2,
        color: "#416fdc",
        expandConstantAcrossAxis: true,
        tooltipText: formatPayoutLineTooltip(expiryLine.tooltip),
      });
    }
    if (snapshotLine?.data?.length) {
      result.push({
        name: snapshotName,
        data: snapshotLine.data,
        lineWidth: 3,
        color: "#ffd700",
        expandConstantAcrossAxis: true,
        tooltipText: formatPayoutLineTooltip(snapshotLine.tooltip),
      });
    }
    return result;
  }

  if (selectedMetric === PAYOUT_ANALYSIS_METRICS.BREAKEVEN) {
    const LEG_COLORS = ["#52c41a", "#3995ff", "#ffa500", "#ff4d4f", "#722ed1", "#13c2c2"];
    return lines
      .filter((line) => line?.data?.length)
      .map((line, index) => ({
        name: line.label ?? `Leg ${index + 1}`,
        data: line.data,
        lineWidth: 2,
        color: LEG_COLORS[index % LEG_COLORS.length],
        expandConstantAcrossAxis: true,
      }));
  }

  const line = lines[0];
  if (!line?.data?.length) return [];

  return [
    {
      name: line.label ?? selectedMetric,
      data: line.data,
      lineWidth: 2,
      color: "#3995ff",
      expandConstantAcrossAxis: true,
    },
  ];
}

export function formatPayoutAnalysisApiErrors(errors) {
  if (!errors || typeof errors !== "object") return null;
  return Object.entries(errors)
    .flatMap(([field, messages]) =>
      (Array.isArray(messages) ? messages : [messages]).map(
        (msg) => `${field}: ${msg}`
      )
    )
    .join("\n");
}
